
import { api } from './base';
import { withIdempotencyKey } from './idempotency';
import type { Ticket } from '@/entities/ticket';
import { getValidToken } from '@/features/auth';
import { config } from '@/shared/lib/config';
import { compressImageFile } from '@/shared/lib/compress-image';
import type { FormFileValue } from './form-schema';

export interface TicketDraft {
  id: string;
  categoryId: string | null;
  subcategoryId: string | null;
  subject: string | null;
  description: string | null;
  formData: Record<string, unknown>;
  updatedAt: string;
}

export interface DraftPayload {
  subject?: string;
  description?: string;
  subcategoryId?: string;
  formData?: Record<string, unknown>;
}

export const draftsApi = {
  get: () => api.get<TicketDraft | null>('/api/v1/me/draft'),

  save: (payload: DraftPayload) => api.put<TicketDraft>('/api/v1/me/draft', payload),

  discard: () => api.delete<unknown>('/api/v1/me/draft'),

  uploadFile: (file: File, onProgress?: (percent: number) => void): Promise<FormFileValue> =>
    new Promise((resolve, reject) => {
      Promise.all([getValidToken(), compressImageFile(file)])
        .then(([token, compressed]) => {
          const form = new FormData();
          form.append('file', compressed);
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${config.apiUrl}/api/v1/me/draft/files`);
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
          };
          xhr.onload = () => {
            try {
              const json = JSON.parse(xhr.responseText) as { data?: FormFileValue; error?: { message?: string } };
              if (xhr.status >= 200 && xhr.status < 300 && json.data) resolve(json.data);
              else reject(new Error(json.error?.message ?? `Ошибка загрузки (${xhr.status})`));
            } catch {
              reject(new Error(`Ошибка загрузки (${xhr.status})`));
            }
          };
          xhr.onerror = () => reject(new Error('Сетевая ошибка при загрузке файла'));
          xhr.send(form);
        })
        .catch(reject);
    }),

  deleteFile: (id: string) => api.delete<unknown>(`/api/v1/me/draft/files/${id}`),

  submit: () =>
    withIdempotencyKey('drafts:submit', {}, (idempotencyKey) =>
      api.post<Ticket>('/api/v1/me/draft/submit', {}, { idempotencyKey }),
    ),
};
